import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { type HaulRoute } from 'api/haul-route.api';
import { formatDistance, formatSpeedRange } from './roadsImporter.helpers';

interface ViewRouteDialogProps {
  target: HaulRoute | null;
  onClose: () => void;
  onEdit: (route: HaulRoute) => void;
}

export function ViewRouteDialog({
  target,
  onClose,
  onEdit,
}: ViewRouteDialogProps): JSX.Element {
  const { t } = useTranslation();

  const rows: Array<[string, string | number]> = [
    [
      t('roads_importer_field_distance'),
      target?.distanceMeters != null
        ? formatDistance(target.distanceMeters)
        : '—',
    ],
    [
      t('roads_importer_field_minutes'),
      target?.minutes != null
        ? `${target.minutes} ${t('roads_importer_minutes_suffix')}`
        : '—',
    ],
    [t('roads_importer_field_capacity'), target?.truckCapacity ?? '—'],
    [
      t('roads_importer_field_weight_capacity'),
      target?.weightCapacityInTon ?? '—',
    ],
    [
      t('roads_importer_field_speed_limit'),
      formatSpeedRange(target?.speedLimitMinKph, target?.speedLimitMaxKph),
    ],
    [t('roads_importer_field_lanes'), target?.numberOfLanes ?? '—'],
    [
      t('roads_importer_field_road_condition'),
      target?.roadCondition
        ? t(`roads_importer_condition_${target.roadCondition}`)
        : '—',
    ],
  ];

  return (
    <Dialog open={target !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_view_route_title', { name: target?.routeKey ?? '' })}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}
      >
        {rows.map(([label, value]) => (
          <Box key={label}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {value}
            </Typography>
          </Box>
        ))}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('roads_importer_field_geo_suggestions')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {target?.geoEngineerSuggestions?.trim()
              ? target.geoEngineerSuggestions
              : '—'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => {
            if (target) onEdit(target);
          }}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_menu_edit_route')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
